import type { LucideIcon } from 'lucide-react';
import type { UserRole } from '@/api/auth';
import {
  Activity,
  Briefcase,
  Eye,
  Brain,
  TrendingUp,
  BarChart3,
  Radar,
  Wallet,
  ShieldAlert,
  ScrollText,
  Newspaper,
  HeartPulse,
  MessageSquareQuote,
} from 'lucide-react';

// Single source of truth for navigation. Keep paths in sync with App.tsx.
export interface RouteDef {
  path: string;
  label: string;
  icon: LucideIcon;
  group: 'Today' | 'Analyze' | 'Operate';
  /** Show in bottom tab bar on mobile (max 5) */
  mobileTab?: boolean;
  /** Second key in a "g + X" jump sequence (e.g. 'h' for Holdings). Lowercase, single char. */
  shortcut?: string;
  /** Minimum role required to see / reach this route. Server enforces independently. */
  requiredRole?: UserRole;
}

export const ROUTES: RouteDef[] = [
  // Today — pages you open during market hours
  { path: '/live',        label: 'Live Console',  icon: Activity,           group: 'Today',   mobileTab: true, shortcut: 'l' },
  { path: '/holdings',    label: 'Holdings',      icon: Briefcase,          group: 'Today',   mobileTab: true, shortcut: 'h' },
  { path: '/watchlist',   label: 'Watchlist',     icon: Eye,                group: 'Today',   mobileTab: true, shortcut: 'w' },
  { path: '/agents',      label: 'Agents',        icon: Brain,              group: 'Today',   mobileTab: true, shortcut: 'a' },

  // Analyze — retrospective
  { path: '/performance', label: 'Performance',   icon: TrendingUp,         group: 'Analyze', shortcut: 'p' },
  { path: '/analytics',   label: 'Analytics',     icon: BarChart3,          group: 'Analyze', shortcut: 'y' },
  { path: '/trades',      label: 'Trade Journal', icon: ScrollText,         group: 'Analyze', shortcut: 't' },
  { path: '/reflections', label: 'Reflections',   icon: MessageSquareQuote, group: 'Analyze', shortcut: 'f' },

  // Operate — tools (operator-only)
  { path: '/scanners',    label: 'Scanners',      icon: Radar,              group: 'Operate', shortcut: 'd', requiredRole: 'OPERATOR' },
  { path: '/cost',        label: 'Cost & Tokens', icon: Wallet,             group: 'Operate', shortcut: 'c', requiredRole: 'OPERATOR' },
  { path: '/risk',        label: 'Risk Posture',  icon: ShieldAlert,        group: 'Operate', shortcut: 'r', requiredRole: 'OPERATOR' },
  { path: '/news',        label: 'News & Research', icon: Newspaper,        group: 'Operate', shortcut: 'n', requiredRole: 'OPERATOR' },
  { path: '/system',      label: 'System Health', icon: HeartPulse,         group: 'Operate', shortcut: 's', requiredRole: 'OPERATOR' },
];

export const MOBILE_TABS = ROUTES.filter((r) => r.mobileTab);

/**
 * Filter routes by the caller's role. Routes with no {@code requiredRole} are
 * always visible; routes with one are only visible when the user has it.
 * Server-side Spring Security enforces this independently — this is for UX,
 * not security.
 */
export function visibleRoutes(role: UserRole | null): RouteDef[] {
  return ROUTES.filter((r) => !r.requiredRole || r.requiredRole === role);
}
